/**
 * Usage analytics — the seam, not the implementation.
 *
 * Every call site in the app talks to `track()` and nothing else. Where the
 * events actually go is decided once, at startup, by whatever sink is installed
 * here. That indirection is the whole point: moving to a different backend, or
 * turning analytics off entirely, changes one line and leaves every call site
 * untouched.
 *
 * The default sink discards. A page that renders before startup has run, a
 * test, a server-side render — none of them should have to know analytics
 * exists, and none of them should send anything.
 */

import type { EventName, EventProps, Sink } from "./events";

export type {
  AnalyticsEvent,
  EventName,
  EventProps,
  Sink,
} from "./events";
export { EVENT_NAMES } from "./events";

/** Discards everything. Also what an opted-out resident gets. */
export const NO_OP_SINK: Sink = {
  track: () => {},
  flush: async () => {},
};

let sink: Sink = NO_OP_SINK;

export const setAnalyticsSink = (next: Sink): void => {
  sink = next;
};

export const getAnalyticsSink = (): Sink => sink;

/**
 * Record that something happened.
 *
 * Never throws and never returns a promise: analytics must not be able to break
 * the thing it is measuring, and no call site should have to remember that.
 */
export const track = (name: EventName, props?: EventProps): void => {
  try {
    sink.track(name, props);
  } catch (e) {
    console.error("Analytics track failed:", e);
  }
};

export const flush = (): Promise<void> => {
  try {
    return sink.flush();
  } catch (e) {
    console.error("Analytics flush failed:", e);
    return Promise.resolve();
  }
};

/**
 * Where the opt-out lives.
 *
 * Its own key rather than a field inside the notification preferences: those
 * are synced to the server as part of a push subscription, and a resident who
 * has turned analytics off should not have that choice travel anywhere.
 */
const OPT_OUT_KEY = "sortsofort_analytics_disabled";

/**
 * Fired after `setAnalyticsDisabled` writes, so anything holding the
 * preference in state (Analytics.tsx) can react without waiting for the next
 * launch to re-read localStorage.
 */
const PREFERENCE_EVENT = "sortsofort:analytics-preference";

export function isAnalyticsDisabled(): boolean {
  try {
    return localStorage.getItem(OPT_OUT_KEY) === "true";
  } catch {
    // Storage can throw outright in a locked-down browser. Defaulting to
    // "enabled" on a read failure would ignore a choice the resident made.
    return true;
  }
}

export function setAnalyticsDisabled(disabled: boolean): void {
  try {
    if (disabled) {
      localStorage.setItem(OPT_OUT_KEY, "true");
    } else {
      localStorage.removeItem(OPT_OUT_KEY);
    }
  } catch (e) {
    console.error("Could not save the analytics preference:", e);
  }
  window.dispatchEvent(new CustomEvent(PREFERENCE_EVENT, { detail: { disabled } }));
}

/**
 * Notify a listener whenever the opt-out changes. Returns the unsubscribe
 * function, so a component can wire this into a single `useEffect`.
 */
export function subscribeAnalyticsPreference(
  callback: (disabled: boolean) => void,
): () => void {
  const handler = (event: Event) => {
    callback((event as CustomEvent<{ disabled: boolean }>).detail.disabled);
  };
  window.addEventListener(PREFERENCE_EVENT, handler);
  return () => window.removeEventListener(PREFERENCE_EVENT, handler);
}

